import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  Button,
  Chip,
  DataTable,
  DEFAULT_PAGE_SIZE,
  Field,
  FilterChip,
  Kpi,
  Modal,
  Pager,
  RoleChip,
  SearchInput,
  Select,
  Skeleton,
  StateBlock,
  useToast,
  type Column,
} from '@w7/ui'
import { useMemo, useState } from 'react'
import {
  cmApi,
  type ConsoleMfaMethod,
  type ConsoleMfaRow,
  type PlayerConsoleMfaMethod,
  type PlayerConsoleMfaRow,
} from '@/api/cmApi'
import { useCmMe } from '@/api/hooks/useCmMe'
import { isStaffCaller } from '@/app/staffAccess'
import { authHeaders } from '@/auth/session'
import { CmPanel } from '@/features/catalog/CmPanel'
import {
  ROLE_LABEL,
  ROLE_SHORT,
  levelToRole,
  type Role,
} from '@/features/hierarchy/hierarchy'
import {
  agentLine,
  confirmCopy,
  methodLabel,
  methodTone,
  playerConfirmCopy,
  playerLine,
  type ConsoleMfaConfirmIntent,
  type PlayerConsoleMfaConfirmIntent,
} from './consoleMfaCopy'
import './console-mfa.css'

type Audience = 'agents' | 'players'
type MfaFilter = 'all' | 'on' | 'off'
type AgentMethodFilter = 'any' | ConsoleMfaMethod
type PlayerMethodFilter = 'any' | PlayerConsoleMfaMethod
type LevelFilter = 'all' | '2' | '3' | '4' | '5'
type DrillCrumb = { agentId: string; username: string }

const PAGE_SIZE = DEFAULT_PAGE_SIZE

const LEVEL_OPTIONS: { value: LevelFilter; label: string }[] = [
  { value: 'all', label: 'All tiers' },
  { value: '2', label: 'Triple Master' },
  { value: '3', label: 'Double Master' },
  { value: '4', label: 'Super Master' },
  { value: '5', label: 'Master' },
]

/**
 * CM owner only — Agents (Keycloak TM→Master) + Players (portal MFA via shared DB).
 */
export function ConsoleMfaPage() {
  const toast = useToast()
  const qc = useQueryClient()
  const me = useCmMe()
  const staffBlocked = isStaffCaller(me.data?.caller)

  const [audience, setAudience] = useState<Audience>('agents')
  const [qDraft, setQDraft] = useState('')
  const [q, setQ] = useState('')
  const [mfa, setMfa] = useState<MfaFilter>('all')
  const [agentMethod, setAgentMethod] = useState<AgentMethodFilter>('any')
  const [playerMethod, setPlayerMethod] = useState<PlayerMethodFilter>('any')
  const [level, setLevel] = useState<LevelFilter>('all')
  const [page, setPage] = useState(1)
  const [drill, setDrill] = useState<DrillCrumb[]>([])
  const [agentIntent, setAgentIntent] = useState<ConsoleMfaConfirmIntent | null>(null)
  const [playerIntent, setPlayerIntent] = useState<PlayerConsoleMfaConfirmIntent | null>(null)

  const parentId = drill.length ? drill[drill.length - 1]!.agentId : undefined
  const canQuery = Boolean(me.data) && !staffBlocked
  const isAgents = audience === 'agents'

  const switchAudience = (next: Audience) => {
    if (next === audience) return
    setAudience(next)
    setQDraft('')
    setQ('')
    setMfa('all')
    setAgentMethod('any')
    setPlayerMethod('any')
    setLevel('all')
    setDrill([])
    setPage(1)
    setAgentIntent(null)
    setPlayerIntent(null)
  }

  const agentListBody = {
    q: q || undefined,
    mfa,
    method: agentMethod,
    level: level === 'all' ? undefined : Number(level),
    ...(parentId ? { parentId } : {}),
    page,
    pageSize: PAGE_SIZE,
  }

  const playerListBody = {
    q: q || undefined,
    mfa,
    method: playerMethod,
    page,
    pageSize: PAGE_SIZE,
  }

  const agentSummaryQ = useQuery({
    queryKey: ['cm', 'console-mfa', 'summary'],
    enabled: canQuery && isAgents,
    staleTime: 60_000,
    queryFn: () => cmApi.consoleMfaSummary(authHeaders(), {}),
  })

  const agentListQ = useQuery({
    queryKey: ['cm', 'console-mfa', 'list', q, mfa, agentMethod, level, parentId ?? 'root', page],
    enabled: canQuery && isAgents,
    queryFn: () => cmApi.consoleMfaList(authHeaders(), agentListBody),
  })

  const playerSummaryQ = useQuery({
    queryKey: ['cm', 'console-mfa', 'players', 'summary'],
    enabled: canQuery && !isAgents,
    staleTime: 60_000,
    queryFn: () => cmApi.playerConsoleMfaSummary(authHeaders(), {}),
  })

  const playerListQ = useQuery({
    queryKey: ['cm', 'console-mfa', 'players', 'list', q, mfa, playerMethod, page],
    enabled: canQuery && !isAgents,
    queryFn: () => cmApi.playerConsoleMfaList(authHeaders(), playerListBody),
  })

  const drillInto = (row: ConsoleMfaRow) => {
    if (!row.childCount) return
    setDrill((prev) => [...prev, { agentId: row.agentId, username: row.username || row.displayName }])
    setPage(1)
  }

  const drillTo = (index: number) => {
    setDrill((prev) => (index < 0 ? [] : prev.slice(0, index + 1)))
    setPage(1)
  }

  const setAgentM = useMutation({
    mutationFn: (body: {
      agentId: string
      action: ConsoleMfaConfirmIntent['kind']
      method?: ConsoleMfaMethod
    }) =>
      cmApi.consoleMfaSet(authHeaders(), {
        agentId: body.agentId,
        action: body.action,
        method: body.method,
        confirm: true,
      }),
    onSuccess: async (res) => {
      toast.success(res.message)
      setAgentIntent(null)
      await Promise.all([
        qc.invalidateQueries({ queryKey: ['cm', 'console-mfa', 'summary'] }),
        qc.invalidateQueries({ queryKey: ['cm', 'console-mfa', 'list'] }),
      ])
    },
    onError: (err) => toast.error(err instanceof Error ? err.message : String(err)),
  })

  const setPlayerM = useMutation({
    mutationFn: (body: {
      playerId: string
      action: PlayerConsoleMfaConfirmIntent['kind']
      method?: PlayerConsoleMfaMethod
    }) =>
      cmApi.playerConsoleMfaSet(authHeaders(), {
        playerId: body.playerId,
        action: body.action,
        method: body.method,
        confirm: true,
      }),
    onSuccess: async (res) => {
      toast.success(res.message)
      setPlayerIntent(null)
      await Promise.all([
        qc.invalidateQueries({ queryKey: ['cm', 'console-mfa', 'players', 'summary'] }),
        qc.invalidateQueries({ queryKey: ['cm', 'console-mfa', 'players', 'list'] }),
      ])
    },
    onError: (err) => toast.error(err instanceof Error ? err.message : String(err)),
  })

  const agentRows = agentListQ.data?.rows ?? []
  const playerRows = playerListQ.data?.rows ?? []
  const agentSummary = agentSummaryQ.data?.summary
  const playerSummary = playerSummaryQ.data?.summary
  const total = isAgents ? (agentListQ.data?.total ?? 0) : (playerListQ.data?.total ?? 0)
  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE))
  const narrowed = isAgents
    ? Boolean(q || mfa !== 'all' || agentMethod !== 'any' || level !== 'all' || drill.length)
    : Boolean(q || mfa !== 'all' || playerMethod !== 'any')
  const tablePending = isAgents ? agentListQ.isFetching : playerListQ.isFetching
  const refreshBusy = isAgents
    ? agentSummaryQ.isFetching || agentListQ.isFetching
    : playerSummaryQ.isFetching || playerListQ.isFetching
  const listError = isAgents ? agentListQ.isError : playerListQ.isError
  const listErrorMsg = isAgents
    ? (agentListQ.error as Error)?.message
    : (playerListQ.error as Error)?.message
  const summaryError = isAgents ? agentSummaryQ.isError : playerSummaryQ.isError
  const summaryErrorMsg = isAgents
    ? (agentSummaryQ.error as Error)?.message
    : (playerSummaryQ.error as Error)?.message

  const refreshAll = () => {
    if (isAgents) {
      void agentSummaryQ.refetch()
      void agentListQ.refetch()
    } else {
      void playerSummaryQ.refetch()
      void playerListQ.refetch()
    }
  }

  const agentColumns = useMemo<Column<ConsoleMfaRow>[]>(
    () => [
      {
        id: 'user',
        header: 'Agent',
        width: 280,
        cell: (row) => {
          const { name, aside } = agentLine(row)
          const canDrill = row.childCount > 0
          const line = (
            <span className="cm-mfa-user-line">
              <span className="cm-mfa-user-name">{name}</span>
              {aside ? <span className="cm-mfa-user-aside"> · {aside}</span> : null}
              {canDrill ? (
                <span className="cm-mfa-user-kids" title={`${row.childCount} under this agent`}>
                  {' '}
                  ({row.childCount})
                </span>
              ) : null}
            </span>
          )
          if (!canDrill) return <div className="cm-mfa-user">{line}</div>
          return (
            <button
              type="button"
              className="cm-mfa-user cm-mfa-user-link"
              onClick={() => drillInto(row)}
              title={`Open downline under ${name}`}
              aria-label={`Open downline under ${name}`}
            >
              {line}
            </button>
          )
        },
        sortValue: (row) => row.username,
      },
      {
        id: 'tier',
        header: 'Tier',
        width: 88,
        cell: (row) => {
          const role = levelToRole(row.level) as Role
          return <RoleChip role={role} label={ROLE_SHORT[role]} />
        },
        sortValue: (row) => row.level,
      },
      {
        id: 'status',
        header: '2FA',
        width: 100,
        cell: (row) => {
          if (!row.found) {
            return (
              <Chip size="sm" tone="muted" title="No Keycloak login found">
                Unknown
              </Chip>
            )
          }
          return (
            <Chip size="sm" tone={row.mfaEnabled ? 'profit' : 'muted'}>
              {row.mfaEnabled ? 'On' : 'Off'}
            </Chip>
          )
        },
      },
      {
        id: 'methods',
        header: 'Methods',
        width: 200,
        cell: (row) => (
          <div className="cm-mfa-methods">
            {!row.methods.length ? (
              <span className="cm-hint">—</span>
            ) : (
              row.methods.map((m) => (
                <button
                  key={m}
                  type="button"
                  className="cm-mfa-method-chip"
                  title={`Remove ${methodLabel(m)}`}
                  disabled={!row.found || setAgentM.isPending}
                  onClick={() => setAgentIntent({ kind: 'disable-method', row, method: m })}
                >
                  <Chip size="sm" tone={methodTone(m)}>
                    {methodLabel(m)} ✕
                  </Chip>
                </button>
              ))
            )}
            {row.setupRequired ? (
              <Chip size="sm" tone="warn">
                Setup next login
              </Chip>
            ) : null}
          </div>
        ),
      },
      {
        id: 'actions',
        header: 'Control',
        width: 280,
        cell: (row) => (
          <div className="cm-mfa-actions">
            {row.mfaEnabled ? (
              <Button
                variant="danger"
                size="sm"
                disabled={!row.found || setAgentM.isPending}
                onClick={() => setAgentIntent({ kind: 'disable-all', row })}
              >
                Turn off all
              </Button>
            ) : (
              <Button
                variant="primary"
                size="sm"
                disabled={!row.found || setAgentM.isPending}
                onClick={() => setAgentIntent({ kind: 'require-setup', row })}
              >
                Require setup
              </Button>
            )}
            {row.setupRequired ? (
              <Button
                variant="secondary"
                size="sm"
                disabled={setAgentM.isPending}
                onClick={() => setAgentIntent({ kind: 'clear-setup', row })}
              >
                Clear force
              </Button>
            ) : null}
          </div>
        ),
      },
    ],
    [setAgentM.isPending],
  )

  const playerColumns = useMemo<Column<PlayerConsoleMfaRow>[]>(
    () => [
      {
        id: 'user',
        header: 'Player',
        width: 320,
        cell: (row) => {
          const { name, aside } = playerLine(row)
          return (
            <div className="cm-mfa-user">
              <span className="cm-mfa-user-line">
                <span className="cm-mfa-user-name">{name}</span>
                {aside ? <span className="cm-mfa-user-aside"> · {aside}</span> : null}
              </span>
            </div>
          )
        },
        sortValue: (row) => row.username,
      },
      {
        id: 'status',
        header: '2FA',
        width: 100,
        cell: (row) => (
          <Chip size="sm" tone={row.mfaEnabled ? 'profit' : 'muted'}>
            {row.mfaEnabled ? 'On' : 'Off'}
          </Chip>
        ),
      },
      {
        id: 'methods',
        header: 'Methods',
        width: 220,
        cell: (row) => (
          <div className="cm-mfa-methods">
            {!row.methods.length ? (
              <span className="cm-hint">—</span>
            ) : (
              row.methods.map((m) => (
                <button
                  key={m}
                  type="button"
                  className="cm-mfa-method-chip"
                  title={`Remove ${methodLabel(m)}`}
                  disabled={setPlayerM.isPending}
                  onClick={() => setPlayerIntent({ kind: 'disable-method', row, method: m })}
                >
                  <Chip size="sm" tone={methodTone(m)}>
                    {methodLabel(m)} ✕
                  </Chip>
                </button>
              ))
            )}
          </div>
        ),
      },
      {
        id: 'actions',
        header: 'Control',
        width: 180,
        cell: (row) => (
          <div className="cm-mfa-actions">
            {row.mfaEnabled ? (
              <Button
                variant="danger"
                size="sm"
                disabled={setPlayerM.isPending}
                onClick={() => setPlayerIntent({ kind: 'disable-all', row })}
              >
                Turn off all
              </Button>
            ) : (
              <Button
                variant="primary"
                size="sm"
                disabled={setPlayerM.isPending}
                onClick={() => setPlayerIntent({ kind: 'require-setup', row })}
              >
                Require setup
              </Button>
            )}
            {row.setupRequired ? (
              <Button
                variant="secondary"
                size="sm"
                disabled={setPlayerM.isPending}
                onClick={() => setPlayerIntent({ kind: 'clear-setup', row })}
              >
                Clear force
              </Button>
            ) : null}
          </div>
        ),
      },
    ],
    [setPlayerM.isPending],
  )

  const agentCopy = agentIntent ? confirmCopy(agentIntent) : null
  const playerCopy = playerIntent ? playerConfirmCopy(playerIntent) : null
  const panelSubtitle = isAgents
    ? 'Search TM and downline agents — see which factors are on, remove one method, or turn all off. Owner CM only.'
    : 'Search portal players — check App / SMS / Recovery, remove one method, or turn all off. Owner CM only.'

  if (me.isLoading || (me.isFetching && !me.data)) {
    return (
      <CmPanel
        title="Console 2FA"
        subtitle="Company Master only — agents (Keycloak) and portal players"
        wide
      >
        <div className="cm-security">
          <StateBlock icon="settings" title="Loading Console 2FA…">
            Checking Company Master access.
          </StateBlock>
        </div>
      </CmPanel>
    )
  }

  if (staffBlocked) {
    return (
      <CmPanel
        title="Console 2FA"
        subtitle="Company Master only — agents (Keycloak) and portal players"
        wide
      >
        <div className="cm-security">
          <StateBlock icon="lock" title="Company Master only" tone="error">
            Staff Access Level users cannot change downline or player 2FA. Sign in as the owner CM
            account.
          </StateBlock>
        </div>
      </CmPanel>
    )
  }

  return (
    <CmPanel
      title="Console 2FA"
      subtitle={panelSubtitle}
      wide
      actions={
        <Button
          variant="secondary"
          size="sm"
          icon="refresh"
          busy={refreshBusy}
          onClick={refreshAll}
        >
          Refresh
        </Button>
      }
    >
      <div className="cm-security cm-mfa-page">
        <div className="cm-mfa-filters" role="tablist" aria-label="2FA audience">
          {(
            [
              ['agents', 'Agents'],
              ['players', 'Players'],
            ] as const
          ).map(([id, label]) => (
            <FilterChip
              key={id}
              active={audience === id}
              onClick={() => switchAudience(id)}
            >
              {label}
            </FilterChip>
          ))}
        </div>

        {isAgents ? (
          <div className="cm-mfa-kpis" aria-busy={agentSummaryQ.isFetching || undefined}>
            <Kpi size="sm" label="Agents" value={String(agentSummary?.total ?? '—')} meta="TM → Master" />
            <Kpi
              size="sm"
              label="2FA on"
              value={String(agentSummary?.mfaOn ?? '—')}
              tone="profit"
              meta={agentSummary ? `${agentSummary.mfaOff} off` : undefined}
              accent
            />
            <Kpi size="sm" label="App" value={String(agentSummary?.totp ?? '—')} tone="brand" />
            <Kpi size="sm" label="Passkey" value={String(agentSummary?.passkey ?? '—')} tone="info" />
            <Kpi
              size="sm"
              label="Setup pending"
              value={String(agentSummary?.setupRequired ?? '—')}
              tone="warn"
            />
          </div>
        ) : (
          <div className="cm-mfa-kpis" aria-busy={playerSummaryQ.isFetching || undefined}>
            <Kpi size="sm" label="Players" value={String(playerSummary?.total ?? '—')} meta="Portal" />
            <Kpi
              size="sm"
              label="2FA on"
              value={String(playerSummary?.mfaOn ?? '—')}
              tone="profit"
              meta={playerSummary ? `${playerSummary.mfaOff} off` : undefined}
              accent
            />
            <Kpi size="sm" label="App" value={String(playerSummary?.totp ?? '—')} tone="brand" />
            <Kpi size="sm" label="SMS" value={String(playerSummary?.sms ?? '—')} tone="info" />
            <Kpi
              size="sm"
              label="Recovery"
              value={String(playerSummary?.recovery ?? '—')}
              tone="muted"
            />
          </div>
        )}

        <div className="cm-toolbar cm-mfa-toolbar">
          <SearchInput
            label={isAgents ? 'Search agents' : 'Search players'}
            placeholder="Username, name, or email"
            value={qDraft}
            width={260}
            onChange={(value) => {
              setQDraft(value)
              setQ(value.trim())
              setPage(1)
            }}
          />
          <div className="cm-mfa-filters" role="group" aria-label="2FA status">
            {(
              [
                ['all', 'All'],
                ['on', 'On'],
                ['off', 'Off'],
              ] as const
            ).map(([id, label]) => (
              <FilterChip
                key={id}
                active={mfa === id}
                onClick={() => {
                  setMfa(id)
                  setPage(1)
                }}
              >
                {label}
              </FilterChip>
            ))}
          </div>
          <div className="cm-mfa-filters" role="group" aria-label="Method">
            {isAgents
              ? (
                  [
                    ['any', 'Any method'],
                    ['totp', 'App'],
                    ['passkey', 'Passkey'],
                    ['recovery', 'Recovery'],
                  ] as const
                ).map(([id, label]) => (
                  <FilterChip
                    key={id}
                    active={agentMethod === id}
                    onClick={() => {
                      setAgentMethod(id)
                      setPage(1)
                    }}
                  >
                    {label}
                  </FilterChip>
                ))
              : (
                  [
                    ['any', 'Any method'],
                    ['totp', 'App'],
                    ['sms', 'SMS'],
                    ['recovery', 'Recovery'],
                  ] as const
                ).map(([id, label]) => (
                  <FilterChip
                    key={id}
                    active={playerMethod === id}
                    onClick={() => {
                      setPlayerMethod(id)
                      setPage(1)
                    }}
                  >
                    {label}
                  </FilterChip>
                ))}
          </div>
          {isAgents ? (
            <Field label="Tier">
              {({ id, describedBy }) => (
                <Select
                  id={id}
                  aria-describedby={describedBy}
                  value={level}
                  onChange={(e) => {
                    setLevel(e.target.value as LevelFilter)
                    setPage(1)
                  }}
                  options={LEVEL_OPTIONS}
                />
              )}
            </Field>
          ) : null}
        </div>

        {isAgents ? (
          <nav className="cm-mfa-crumbs" aria-label="Hierarchy path">
            <button
              type="button"
              className="cm-mfa-crumb"
              data-active={drill.length === 0 ? 'true' : undefined}
              onClick={() => drillTo(-1)}
            >
              Top (TM)
            </button>
            {drill.map((crumb, i) => (
              <span key={crumb.agentId} className="cm-mfa-crumb-wrap">
                <span className="cm-mfa-crumb-sep" aria-hidden>
                  /
                </span>
                <button
                  type="button"
                  className="cm-mfa-crumb"
                  data-active={i === drill.length - 1 ? 'true' : undefined}
                  onClick={() => drillTo(i)}
                >
                  {crumb.username}
                </button>
              </span>
            ))}
          </nav>
        ) : null}

        {summaryError ? (
          <div className="cm-hint" role="alert">
            <StateBlock icon="error" title="Could not load 2FA summary" tone="error">
              {summaryErrorMsg || 'Request failed.'}
            </StateBlock>
            <Button type="button" variant="secondary" size="sm" onClick={refreshAll}>
              Retry summary
            </Button>
          </div>
        ) : null}

        {listError && !tablePending && !(isAgents ? agentRows.length : playerRows.length) ? (
          <div className="cm-hint" role="alert">
            <StateBlock
              icon="error"
              title={isAgents ? 'Could not load console 2FA' : 'Could not load player 2FA'}
              tone="error"
            >
              {listErrorMsg || 'Request failed.'}
            </StateBlock>
            <Button type="button" variant="secondary" size="sm" onClick={refreshAll}>
              Retry
            </Button>
          </div>
        ) : tablePending ? (
          <div
            className="cm-mfa-skel"
            role="status"
            aria-label={isAgents ? 'Loading agents' : 'Loading players'}
            aria-busy="true"
          >
            {Array.from({ length: 8 }, (_, i) => (
              <div key={i} className="cm-mfa-skel-row">
                <Skeleton height={40} width="100%" radius={6} />
              </div>
            ))}
          </div>
        ) : isAgents ? (
          <>
            <DataTable
              rows={agentRows}
              columns={agentColumns}
              rowKey={(row) => row.agentId}
              label={`${total} agents with console 2FA status`}
              density="compact"
              empty={
                <div className="cm-security-empty">
                  <StateBlock
                    icon="search"
                    title={narrowed ? 'No agents match' : 'No Data Found'}
                    actions={
                      narrowed ? (
                        <Button
                          variant="secondary"
                          size="sm"
                          onClick={() => {
                            setQDraft('')
                            setQ('')
                            setMfa('all')
                            setAgentMethod('any')
                            setLevel('all')
                            setDrill([])
                            setPage(1)
                          }}
                        >
                          Clear filters
                        </Button>
                      ) : undefined
                    }
                  >
                    {narrowed
                      ? 'Clear search or widen filters. Only TM → Master with hierarchy logins appear here.'
                      : 'No TM → Master agents with hierarchy logins were returned for this Company Master.'}
                  </StateBlock>
                </div>
              }
            />
            {total > 0 ? (
              <div className="cm-security-pager">
                <Pager
                  page={Math.min(page, pageCount)}
                  pageCount={pageCount}
                  total={total}
                  pageSize={agentListQ.data?.pageSize ?? PAGE_SIZE}
                  onChange={setPage}
                />
              </div>
            ) : null}
          </>
        ) : (
          <>
            <DataTable
              rows={playerRows}
              columns={playerColumns}
              rowKey={(row) => row.playerId}
              label={`${total} players with portal 2FA status`}
              density="compact"
              empty={
                <div className="cm-security-empty">
                  <StateBlock
                    icon="search"
                    title={narrowed ? 'No players match' : 'No Data Found'}
                    actions={
                      narrowed ? (
                        <Button
                          variant="secondary"
                          size="sm"
                          onClick={() => {
                            setQDraft('')
                            setQ('')
                            setMfa('all')
                            setPlayerMethod('any')
                            setPage(1)
                          }}
                        >
                          Clear filters
                        </Button>
                      ) : undefined
                    }
                  >
                    {narrowed
                      ? 'Clear search or widen filters. Portal members from player.users appear here.'
                      : 'No portal players were returned from the shared player MFA tables.'}
                  </StateBlock>
                </div>
              }
            />
            {total > 0 ? (
              <div className="cm-security-pager">
                <Pager
                  page={Math.min(page, pageCount)}
                  pageCount={pageCount}
                  total={total}
                  pageSize={playerListQ.data?.pageSize ?? PAGE_SIZE}
                  onChange={setPage}
                />
              </div>
            ) : null}
          </>
        )}

        <Modal
          open={agentIntent !== null}
          onClose={() => {
            if (!setAgentM.isPending) setAgentIntent(null)
          }}
          title={agentCopy?.title ?? 'Confirm 2FA change'}
          subtitle="Company Master security control — this changes Keycloak sign-in factors"
          closeDisabled={setAgentM.isPending}
          footer={
            <div className="cm-mfa-modal-footer">
              <Button
                variant="ghost"
                disabled={setAgentM.isPending}
                onClick={() => setAgentIntent(null)}
              >
                Cancel
              </Button>
              <Button
                variant={agentCopy?.danger ? 'danger' : 'primary'}
                busy={setAgentM.isPending}
                disabled={!agentIntent}
                onClick={() => {
                  if (!agentIntent) return
                  setAgentM.mutate({
                    agentId: agentIntent.row.agentId,
                    action: agentIntent.kind,
                    method:
                      agentIntent.kind === 'disable-method' ? agentIntent.method : undefined,
                  })
                }}
              >
                {agentCopy?.confirmLabel ?? 'Confirm'}
              </Button>
            </div>
          }
        >
          <p className="cm-mfa-modal-body">{agentCopy?.body}</p>
          {agentIntent ? (
            <div className="cm-mfa-modal-meta">
              <div>
                <span className="cm-hint">User</span>
                <strong>{agentIntent.row.username}</strong>
              </div>
              <div>
                <span className="cm-hint">Tier</span>
                <strong>{ROLE_LABEL[levelToRole(agentIntent.row.level)]}</strong>
              </div>
              <div>
                <span className="cm-hint">Current</span>
                <strong>
                  {agentIntent.row.mfaEnabled
                    ? agentIntent.row.methods.map(methodLabel).join(', ') || 'On'
                    : 'Off'}
                </strong>
              </div>
            </div>
          ) : null}
        </Modal>

        <Modal
          open={playerIntent !== null}
          onClose={() => {
            if (!setPlayerM.isPending) setPlayerIntent(null)
          }}
          title={playerCopy?.title ?? 'Confirm player 2FA change'}
          subtitle="Company Master security control — this changes portal player 2FA"
          closeDisabled={setPlayerM.isPending}
          footer={
            <div className="cm-mfa-modal-footer">
              <Button
                variant="ghost"
                disabled={setPlayerM.isPending}
                onClick={() => setPlayerIntent(null)}
              >
                Cancel
              </Button>
              <Button
                variant={playerCopy?.danger ? 'danger' : 'primary'}
                busy={setPlayerM.isPending}
                disabled={!playerIntent}
                onClick={() => {
                  if (!playerIntent) return
                  setPlayerM.mutate({
                    playerId: playerIntent.row.playerId,
                    action: playerIntent.kind,
                    method:
                      playerIntent.kind === 'disable-method' ? playerIntent.method : undefined,
                  })
                }}
              >
                {playerCopy?.confirmLabel ?? 'Confirm'}
              </Button>
            </div>
          }
        >
          <p className="cm-mfa-modal-body">{playerCopy?.body}</p>
          {playerIntent ? (
            <div className="cm-mfa-modal-meta">
              <div>
                <span className="cm-hint">Player</span>
                <strong>{playerIntent.row.username}</strong>
              </div>
              <div>
                <span className="cm-hint">Current</span>
                <strong>
                  {playerIntent.row.mfaEnabled
                    ? playerIntent.row.methods.map(methodLabel).join(', ') || 'On'
                    : 'Off'}
                </strong>
              </div>
            </div>
          ) : null}
        </Modal>
      </div>
    </CmPanel>
  )
}
